import React, { useRef } from "react";
import { IconButton } from "@mui/material";
import { useDrag, useDrop } from "react-dnd";

import commonStyles from "../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import listStyles from "../../../../../SwmsMasters/PreStartQuestions/MethodQuestionsStyle.module.css";
import styles from "../pre-start/JobSwmsFormV3PreStart.module.css";
import { getAnswerScopeLabel, getFlagLabel, getResponseTypeLabel } from "../../../../../SwmsMasters/PreStartQuestions/preStartQuestionViewConfig";

const RESCUE_READINESS_DRAG_TYPE_V3 = "SWMS_PRE_START_QUESTION_V3";

function DragHandleVisual({ active }) {
  return (
    <span
      className={`${listStyles.MethodQuestionDragHandle} ${active ? "" : listStyles.MethodQuestionDragHandleDisabled}`.trim()}
      aria-label={active ? "Drag handle" : "Drag handle disabled"}
      role="img"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" className={commonStyles.DragIcon}>
        <path fill="currentColor" d="M288 104C288 81.9 270.1 64 248 64L200 64C177.9 64 160 81.9 160 104L160 152C160 174.1 177.9 192 200 192L248 192C270.1 192 288 174.1 288 152L288 104zM288 296C288 273.9 270.1 256 248 256L200 256C177.9 256 160 273.9 160 296L160 344C160 366.1 177.9 384 200 384L248 384C270.1 384 288 366.1 288 344L288 296zM160 488L160 536C160 558.1 177.9 576 200 576L248 576C270.1 576 288 558.1 288 536L288 488C288 465.9 270.1 448 248 448L200 448C177.9 448 160 465.9 160 488zM480 104C480 81.9 462.1 64 440 64L392 64C369.9 64 352 81.9 352 104L352 152C352 174.1 369.9 192 392 192L440 192C462.1 192 480 174.1 480 152L480 104zM352 296L352 344C352 366.1 369.9 384 392 384L440 384C462.1 384 480 366.1 480 344L480 296C480 273.9 462.1 256 440 256L392 256C369.9 256 352 273.9 352 296zM480 488C480 465.9 462.1 448 440 448L392 448C369.9 448 352 465.9 352 488L352 536C352 558.1 369.9 576 392 576L440 576C462.1 576 480 558.1 480 536L480 488z" />
      </svg>
    </span>
  );
}

export default function JobSwmsV3DraggableRescueReadinessQuestionRow({
  question = {},
  index,
  visibleOrder,
  reorderDisabled,
  moveQuestionRow,
  onAfterDropQuestionRow,
  onOpenEditQuestion
}) {
  const rowRef = useRef(null);
  const handleRef = useRef(null);
  const initialIndexRef = useRef(index);

  const isActive = Boolean(question && question.active);
  const isDisabled = question.disabled === true;
  const isJobSpecificMethod = false;
  const methodLabel = question.plan_name || question.emergency_plan_name || question.plan_title || (question.emergency_plan && (question.emergency_plan.label || question.emergency_plan.title || question.emergency_plan.plan_name)) || (question.emergency_plan_id != null ? `Emergency Plan ${question.emergency_plan_id}` : "Job Specific Question");
  const flagLabel = getFlagLabel(question.response_config);
  const scopeClassName = question.answer_scope === "worker"
    ? listStyles.MethodQuestionScopeBadgePersonal
    : listStyles.MethodQuestionScopeBadgeTeam;

  const [, drop] = useDrop({
    accept: RESCUE_READINESS_DRAG_TYPE_V3,
    hover(draggedItem, monitor) {
      if (!rowRef.current || reorderDisabled || isDisabled) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = rowRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveQuestionRow(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: RESCUE_READINESS_DRAG_TYPE_V3,
    canDrag: isActive && !isDisabled && !reorderDisabled,
    item: () => {
      initialIndexRef.current = index;
      return {
        id: question.id,
        index,
        initialIndex: index
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    end: (draggedItem, monitor) => {
      if (draggedItem) {
        onAfterDropQuestionRow({
          ...draggedItem,
          from: initialIndexRef.current,
          to: draggedItem.index,
          didDrop: monitor.didDrop()
        });
      }
    }
  });

  preview(drop(rowRef));
  drag(handleRef);

  return (
    <div
      ref={rowRef}
      className={`${listStyles.MethodQuestionRow} ${question.active && !isDisabled ? "" : listStyles.MethodQuestionRowInactive}`.trim()}
      style={{ opacity: isDragging ? 0.35 : (isDisabled ? 0.6 : 1) }}
      aria-disabled={isDisabled}
    >
      <div className={listStyles.MethodQuestionOrderWrap}>
        <span ref={handleRef} style={{ display: "inline-flex" }}>
          <DragHandleVisual active={Boolean(question.active) && !isDisabled && !reorderDisabled} />
        </span>
        <span className={listStyles.MethodQuestionOrder}>{visibleOrder}</span>
      </div>

      <div className={listStyles.MethodQuestionMain}>
        <div style={{ marginBottom: "4px" }}>
          <span className={`${styles.PreStartMethodTag} ${isJobSpecificMethod ? styles.PreStartMethodTagJobSpecific : ""}`.trim()}>
            {methodLabel}
          </span>
        </div>
        <div className={listStyles.MethodQuestionText}>{question.question_text || "Untitled question"}</div>
        {question.guidance_text ? (
          <div className={listStyles.MethodQuestionGuidance}>{question.guidance_text}</div>
        ) : null}
        <div className={listStyles.MethodQuestionMeta}>
          <span className={`${listStyles.MethodQuestionScopeBadge} ${scopeClassName}`}>
            {getAnswerScopeLabel(question.answer_scope)}
          </span>
          <span className={listStyles.MethodQuestionBadge}>{getResponseTypeLabel(question.response_config)}</span>
          <span className={listStyles.MethodQuestionBadge}>{question.required ? "Required" : "Optional"}</span>
          {flagLabel ? (
            <span className={`${listStyles.MethodQuestionBadge} ${listStyles.MethodQuestionFlagBadge}`}>{flagLabel}</span>
          ) : null}
        </div>
      </div>

      <div className={listStyles.MethodQuestionActions}>
        <div
          className={`${commonStyles.StatusBox} ${question.active ? commonStyles.ActiveStatus : commonStyles.InActiveStatus}`}
          style={{ padding: "3px 6px", width: "90px", fontSize: "13px", textAlign: "center" }}
        >
          {isDisabled ? "Disabled" : (question.active ? "Active" : "Inactive")}
        </div>
        <IconButton
          className={`${listStyles.MethodQuestionEditButton}`}
          style={{ height: "auto" }}
          disabled={reorderDisabled || isDisabled}
          title="Edit question"
          aria-label={`Edit question ${question.question_text || ""}`.trim()}
          onClick={() => onOpenEditQuestion(question, index)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style={{ width: "22px" }}>
            <path fill="currentColor" d="M416.9 85.2L372 130.1L509.9 268L554.8 223.1C568.4 209.6 576 191.2 576 172C576 152.8 568.4 134.4 554.8 120.9L519.1 85.2C505.6 71.6 487.2 64 468 64C448.8 64 430.4 71.6 416.9 85.2zM338.1 164L122.9 379.1C112.2 389.8 104.4 403.2 100.3 417.8L64.9 545.6C62.6 553.9 64.9 562.9 71.1 569C77.3 575.1 86.2 577.5 94.5 575.2L222.3 539.7C236.9 535.6 250.2 527.9 261 517.1L476 301.9L338.1 164z" />
          </svg>
        </IconButton>
      </div>
    </div>
  );
}

